import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import dayjs, { Dayjs } from "dayjs";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const STALE_HIDDEN_THRESHOLD_MINUTES = 30;

async function isVersionStale(): Promise<boolean> {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    const data = await response.json();
    return data.sha !== import.meta.env.VITE_BUILD_SHA;
  } catch {
    return false;
  }
}

function reloadForNewVersion(trigger: string) {
  console.info(`New app version deployed, reloading (${trigger}). Current build: ${import.meta.env.VITE_BUILD_SHA}`);
  window.location.reload();
}

export function ReloadOnNewVersion() {
  const staleRef = useRef(false);
  const hiddenAtRef = useRef<Dayjs | null>(null);
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const runCheck = async (hiddenSince: Dayjs | null) => {
      const isStale = await isVersionStale();
      if (!isStale) return;

      const hiddenTooLong = hiddenSince !== null && dayjs().diff(hiddenSince, "minute") >= STALE_HIDDEN_THRESHOLD_MINUTES;
      if (hiddenTooLong) {
        reloadForNewVersion("tab was hidden too long");
        return;
      }
      staleRef.current = true;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = dayjs();
        return;
      }
      const hiddenSince = hiddenAtRef.current;
      hiddenAtRef.current = null;
      runCheck(hiddenSince);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      runCheck(null);
    }, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (staleRef.current) {
      reloadForNewVersion("route change");
    }
  }, [pathname, search]);

  return null;
}
